import React from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

import {
    BorderWidths,
    Colors,
    MediaQueries,
    Radii,
    Spacing
} from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";
import { Animations, Timing } from "styles/animations/animations";

import ModalPortal from "common/ModalPortal/ModalPortal";
import CircularProgress from "common/CircularProgress/CircularProgress";

/** Note: Since Modal has fixed height, need to make sure the height of content + height of Footer = Modal Height */

const Wrapper = styled.div`
    position: relative;
    background-color: #ffffff;
    border-radius: ${Radii.soft};
    box-shadow: 0px 1px 3px 0px rgba(38, 38, 38, 0.2);
    :hover {
        box-shadow: 0px 4px 15px 0px rgba(38, 38, 38, 0.2);
    }
    ${Animations.GrowIntoPlace}

    width: 90vw; // I can't seem to set width with 100% and margin 32px, Ask Eric and Tim about a potential solution
    max-width: 470px; // larger than iphoneX (360px)
    margin: 0 auto;
    transition: all 250ms ${Timing.standard};

    @media (min-width: ${MediaQueries.md}) {
        max-width: unset;
        width: 470px;
    }

    @media (min-width: ${MediaQueries.lg}) {
        width: 614px;
    }

    ${({ size }) =>
        size === "small" &&
        css`
            max-width: 344px;
            @media (min-width: ${MediaQueries.md}) {
                width: 344px;
                max-width: unset;
            }
        `}
    ${({ size }) =>
        size === "medium" &&
        css`
            transition: all 350ms ${Timing.standard};
            max-width: unset;
            width: 70vw;
            height: 60vh;
            @media (min-width: ${MediaQueries.md}) {
                width: 70vw;
                height: 60vh;
            }
            @media (min-width: ${MediaQueries.lg}) {
                width: 60vw;
                height: 50vh;
            }
        `}
    ${({ size }) =>
        size === "xlarge" &&
        css`
            transition: all 350ms ${Timing.standard};
            max-width: unset;
            width: 90vw;
            height: 90vh;
            @media (min-width: ${MediaQueries.md}) {
                width: 90vw;
                height: 90vh;
            }
            @media (min-width: ${MediaQueries.lg}) {
                width: 80vw;
                height: 80vh;
            }
        `}
`;

const CloseButton = styled.span`
    position: absolute;
    outline: none;
    cursor: pointer;
    top: 16px;
    right: 16px;

    @media (min-width: ${MediaQueries.lg}) {
        top: 24px;
        right: 24px;
    }
`;

const Content = styled.div`
    padding: ${Spacing[32]} ${Spacing[40]};
    /* height: 232px; */
    /* overflow-y: auto; */
    overflow-x: hidden;
    &::-webkit-scrollbar {
        width: 9px;
    }
    &::-webkit-scrollbar-track {
        border-radius: ${Radii.soft};
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${Colors.neutral200};
        border-radius: ${Radii.soft};
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: ${Colors.neutral400};
    }
    @media (min-width: ${MediaQueries.md}) {
        padding: ${Spacing[32]} ${Spacing[40]};
        /* height: 260px; */
    }
    @media (min-width: ${MediaQueries.lg}) {
        padding: ${Spacing[64]} ${Spacing[48]};
        /* height: 334px; */
    }

    ${({ size }) =>
        size === "drop" &&
        css`
            padding: ${Spacing[64]} ${Spacing[40]} ${Spacing[40]};
            height: 380px;
            @media (min-width: ${MediaQueries.md}) {
                padding: ${Spacing[64]} ${Spacing[24]} ${Spacing[40]};
                height: 580px;
            }
        `}

    ${({ size }) =>
        size === "small" &&
        css`
            padding: ${Spacing[64]} ${Spacing[40]} ${Spacing[40]};
            height: 256px;
            @media (min-width: ${MediaQueries.md}) {
                padding: ${Spacing[64]} ${Spacing[24]} ${Spacing[40]};
                height: 256px;
            }
        `}

    ${({ size }) =>
        size === "medium" &&
        css`
            padding: ${Spacing[64]} ${Spacing[40]} ${Spacing[40]};
            height: 380px;
            @media (min-width: ${MediaQueries.md}) {
                padding: ${Spacing[64]} ${Spacing[24]} ${Spacing[40]};
                height: 380px;
            }
        `}

    ${({ size }) =>
        size === "xlarge" &&
        css`
            padding: ${Spacing[16]} ${Spacing[48]} ${Spacing[24]} ${Spacing[16]};
            height: calc(90vh - 136px);
            @media (min-width: ${MediaQueries.md}) {
                padding: ${Spacing[24]} 56px ${Spacing[32]} ${Spacing[24]};
                height: calc(90vh - 88px);
            }
            @media (min-width: ${MediaQueries.lg}) {
                padding: ${Spacing[48]} 72px ${Spacing[48]} ${Spacing[48]};
                height: calc(80vh - 120px);
            }
        `}

    ${({ disableScroll }) =>
        disableScroll &&
        css`
            overflow-y: hidden;
        `}
`;

const Footer = styled.div`
    background-color: rgba(251, 251, 251, 1); // no such token
    border-top: ${BorderWidths.hairline} solid ${Colors.neutral200};
    border-bottom-right-radius: ${Radii.soft};
    border-bottom-left-radius: ${Radii.soft};

    height: 136px;
    padding: ${Spacing[24]};
    @media (min-width: ${MediaQueries.md}) {
        height: 88px;
        padding: ${Spacing[24]};
        display: flex;
        justify-content: flex-end;
    }
    @media (min-width: ${MediaQueries.lg}) {
        height: 120px;
        padding: ${Spacing[40]} ${Spacing[48]};
    }
`;

const ButtonWrapper = styled.div`
    @media (max-width: ${MediaQueries.md}) {
        button {
            margin: 0 auto;
            width: 100%;
        }

        &:first-of-type {
            margin-bottom: ${Spacing[8]};
        }
    }

    @media (min-width: ${MediaQueries.md}) {
        &:first-of-type {
            margin-right: ${Spacing[16]};
        }
    }
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${Radii.soft};
    background-color: #ffffff;
    opacity: 0.6;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BaseModal = ({
    size,
    primaryButton,
    secondaryButton,
    closeModal,
    children,
    disableScroll,
    loading
}) => {
    return (
        <ModalPortal>
            <Wrapper size={size} loading={loading}>
                {loading && (
                    <Overlay>
                        <CircularProgress />
                    </Overlay>
                )}
                { closeModal && (
                    <CloseButton
                        onClick={closeModal}
                        data-track="analytics"
                        data-bname="close-modal"
                    >
                        <Icon name="close" width={24} color={Colors.neutral600} />
                    </CloseButton>
                )}
                <Content size={size} disableScroll={disableScroll}>
                    {children}
                </Content>
                {(secondaryButton || primaryButton) && (
                    <Footer>
                        <ButtonWrapper>{secondaryButton}</ButtonWrapper>
                        <ButtonWrapper>{primaryButton}</ButtonWrapper>
                    </Footer>
                )}
            </Wrapper>
        </ModalPortal>
    );
};

export default BaseModal;

BaseModal.propTypes = {
    size: PropTypes.oneOf(["small", "default", "xlarge"]),
    primaryButton: PropTypes.node,
    secondaryButton: PropTypes.node,
    closeModal: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    disableScroll: PropTypes.bool,
    loading: PropTypes.bool
};

BaseModal.defaultProps = {
    size: "default",
    primaryButton: undefined,
    secondaryButton: undefined,
    disableScroll: false,
    loading: false
};
